import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

async function geocode(q) {
  if (!q || !TOKEN) return null;
  try {
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=${TOKEN}&limit=1&types=place,region,district,locality,poi`;
    const r = await fetch(url);
    const d = await r.json();
    const c = d?.features?.[0]?.center;
    return Array.isArray(c) ? { lng: c[0], lat: c[1], name: q } : null;
  } catch {
    return null;
  }
}

// Route overview map. Prefers real port coordinates supplied by Terminal49 (points +
// milestones with lat/lng); falls back to geocoding POL/POD names. Terminal49 has no
// live GPS, so this is a route map with a marker at the latest known location.
export function VesselMap({ pol, pod, points = [], milestones = [], statusColor = "#7C3AED" }) {
  const hostRef = useRef(null);
  const mapRef = useRef(null);
  const [state, setState] = useState(TOKEN ? "loading" : "notoken");

  const withCoords = (points || []).filter((p) => p && p.lat != null && p.lng != null);
  const actsWithCoords = (milestones || []).filter((m) => (m.actual || m.completed) && m.lat != null && m.lng != null);
  const latestLocName = (() => {
    const s = (milestones || []).filter((m) => (m.actual || m.completed) && typeof m.location === "string" && m.location.trim());
    return s.length ? s[s.length - 1].location : null;
  })();

  useEffect(() => {
    if (!TOKEN || !hostRef.current) return;
    let cancelled = false;
    (async () => {
      let origin = null, dest = null, cur = null;
      const mids = [];
      if (withCoords.length) {
        origin = withCoords.find((p) => p.role === "origin") || withCoords[0];
        dest = withCoords.find((p) => p.role === "destination") || withCoords[withCoords.length - 1];
        const fin = withCoords.find((p) => p.role === "final");
        if (fin && fin !== dest) mids.push(fin);
      } else {
        const [a, b] = await Promise.all([geocode(pol), geocode(pod)]);
        origin = a; dest = b;
      }
      if (actsWithCoords.length) {
        const m = actsWithCoords[actsWithCoords.length - 1];
        cur = { lat: m.lat, lng: m.lng, name: m.location };
      } else if (!withCoords.length && latestLocName) {
        cur = await geocode(latestLocName);
      }
      if (cancelled) return;
      const ordered = [origin, ...mids, dest].filter(Boolean);
      const pts = [...ordered, cur].filter(Boolean);
      if (pts.length === 0) { setState("empty"); return; }

      mapboxgl.accessToken = TOKEN;
      const map = new mapboxgl.Map({
        container: hostRef.current,
        style: "mapbox://styles/mapbox/light-v11",
        center: [ordered[0]?.lng ?? cur.lng, ordered[0]?.lat ?? cur.lat],
        zoom: 2.5,
        attributionControl: false,
        cooperativeGestures: true,
        preserveDrawingBuffer: true,
      });
      mapRef.current = map;
      map.on("load", () => {
        if (cancelled) return;
        map.resize();
        if (ordered.length >= 2) {
          map.addSource("route", { type: "geojson", data: { type: "Feature", geometry: { type: "LineString", coordinates: ordered.map((p) => [p.lng, p.lat]) } } });
          map.addLayer({ id: "route", type: "line", source: "route", layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-color": statusColor, "line-width": 2.5, "line-dasharray": [2, 1.5] } });
        }
        if (origin) new mapboxgl.Marker({ color: "#64748B" }).setLngLat([origin.lng, origin.lat]).setPopup(new mapboxgl.Popup({ offset: 18 }).setText(`Origin · ${origin.name || pol || ""}`)).addTo(map);
        if (dest) new mapboxgl.Marker({ color: "#0891B2" }).setLngLat([dest.lng, dest.lat]).setPopup(new mapboxgl.Popup({ offset: 18 }).setText(`Destination · ${dest.name || pod || ""}`)).addTo(map);
        if (cur) new mapboxgl.Marker({ color: statusColor }).setLngLat([cur.lng, cur.lat]).setPopup(new mapboxgl.Popup({ offset: 18 }).setText(`Latest · ${cur.name || ""}`)).addTo(map);
        const bounds = new mapboxgl.LngLatBounds();
        pts.forEach((p) => bounds.extend([p.lng, p.lat]));
        if (pts.length > 1) map.fitBounds(bounds, { padding: 56, maxZoom: 6, duration: 0 });
        else { map.setCenter([pts[0].lng, pts[0].lat]); map.setZoom(5); }
        setState("ready");
      });
    })();
    return () => { cancelled = true; if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
    // eslint-disable-next-line
  }, [pol, pod, JSON.stringify(withCoords), JSON.stringify(actsWithCoords.map((m) => [m.lat, m.lng])), statusColor]);

  return (
    <div className="relative h-64 w-full overflow-hidden rounded-2xl border border-slate-200" data-testid="vessel-map">
      <div ref={hostRef} className="absolute inset-0" />
      {state !== "ready" && (
        <div className="absolute inset-0 grid place-items-center bg-slate-50 p-5 text-center text-xs text-muted-foreground">
          {state === "notoken"
            ? "Map unavailable — a Mapbox token (REACT_APP_MAPBOX_TOKEN) hasn't been added yet."
            : state === "empty"
              ? "Route not available to map yet — ports will appear once tracking resolves."
              : "Loading map…"}
        </div>
      )}
    </div>
  );
}
